import { useCallback, useContext, useEffect, useRef } from 'react';

import useDeepMemo from '@exp/hooks/useDeepMemo';

import { ModeConfig } from '../constants/modes';
import { selectConfig, selectUserUuid } from '../redux/context';
import { useAppSelector } from '../redux/hooks';
import useLog from './useLog';
import { getLandingAnalytics, InstanceContext, readOnbCache } from './utilities';

const useAnalytics = () => {
  const { analytics } = useContext(InstanceContext);
  const config: ModeConfig = useAppSelector(selectConfig);
  const log = useLog();

  if (!analytics) {
    log.warn('Analytics collector not configured.');
  }

  return config.toggles.disableAnalytics ? null : analytics;
};

export const useSendEvent = (base = {}) => {
  const analytics = useAnalytics();
  const config: ModeConfig = useAppSelector(selectConfig);
  const uuid: string | undefined = useAppSelector(selectUserUuid);

  const baseDataRef = useRef(base);
  const memoizedData = useDeepMemo(base || {});

  // Read at send time rather than render time: the sign-up cache is written after sign-up resolves
  // and removed the moment the user applies, so resolving it here is what makes the landing
  // identifiers appear and disappear with the cache instead of going stale in a memo.
  const landingRef = useRef({ config, uuid });
  landingRef.current = { config, uuid };

  useEffect(() => {
    baseDataRef.current = memoizedData;
  }, [memoizedData]);

  return useCallback(
    (action: string, data: Record<string, unknown> = {}, ...args: unknown[]) => {
      if (!analytics?.sendEvent) {
        return null;
      }

      const landing = getLandingAnalytics(landingRef.current.config, readOnbCache(landingRef.current.uuid));

      // Landing identifiers sit lowest so an explicit per-call value always wins.
      const decorator = (d: Record<string, unknown>) => ({ ...landing, ...baseDataRef.current, ...d });

      if (Array.isArray(data)) {
        return analytics.sendBulkEvents(action, data.map(decorator));
      }
      return analytics.sendEvent(action, Array.isArray(data) ? data.map(decorator) : decorator(data), ...args);
    },
    [analytics],
  );
};

export default useAnalytics;
